import { stringify } from 'query-string';
import {
    fetchUtils,
    GET_LIST,
    GET_ONE,
    GET_MANY,
    GET_MANY_REFERENCE,
    CREATE,
    UPDATE,
    UPDATE_MANY,
    DELETE,
    DELETE_MANY,
} from 'react-admin';

/**
 * Maps react-admin queries to a simple REST API
 *
 * The REST dialect is similar to the one of FakeRest
 * @see https://github.com/marmelab/FakeRest
 * @example
 * GET_LIST     => GET http://my.api.url/posts?sort=['title','ASC']&range=[0, 24]
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?filter={ids:[123,456,789]}
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts
 * DELETE       => DELETE http://my.api.url/posts/123
 */
export default (apiUrl, httpClient = fetchUtils.fetchJson) => {
    /**
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} { url, options } The HTTP request parameters
     */
    const convertDataRequestToHTTP = (type, resource, params) => {
        if (resource === 'devices_daesaminare') {
            resource = 'devices';
            if (params.filter && !params.filter.status) {
                params.filter['status.codice'] = { $in: ['0', '1', '6', '2', '12'] };
            }
        }
        let url = '';
        const options = {};
        options.headers = new Headers({ 'x-zumo-auth': localStorage.getItem('auth-token') });
        switch (type) {
            case GET_LIST: {
                const { field, order } = params.sort;
                const query = {
                    sort: JSON.stringify([field, order]).replace(/"id"/g, '"_id"'),
                    page: JSON.stringify(params.pagination.page),
                    limit: JSON.stringify(params.pagination.perPage),
                    filter: transformSerialToList(params.filter)
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case GET_ONE:
                if (params.userName) {
                    url = `${apiUrl}/${resource}/userName/${params.userName}`;
                }
                else if (params.name) {
                    url = `${apiUrl}/${resource}/${params.name}`;
                }
                else if (params.workrequestid) {
                    url = `${apiUrl}/${resource}?${stringify(params)}`;
                }
                else if (params.cod_nmu) {
                    const query = {
                        filter: JSON.stringify(params),
                    };
                    url = `${apiUrl}/${resource}?${stringify(query)}`;
                }
                else {
                    url = `${apiUrl}/${resource}/${params.id}`;
                }
                break;
            case GET_MANY: {
                const query = {
                    filter: JSON.stringify(params.filter),
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case GET_MANY_REFERENCE: {
                const { field, order } = params.sort;
                const query = {
                    sort: JSON.stringify([field, order]).replace(/"id"/g, '"_id"'),
                    page: JSON.stringify(params.pagination.page),
                    limit: JSON.stringify(params.pagination.perPage),
                    filter: JSON.stringify({
                        ...params.filter,
                        [params.target]: params.id ? params.id : "",
                    }),
                };
                //Allow to get magmoi info outside mandatory filters (for fields from devicelist for example)
                if(resource === "magmois" && params.target === 'cod_terr_sap') {
                    let updatedFilter = JSON.parse(query.filter);
                    updatedFilter.IgnoreMandatoryFilters = true;
                    query.filter = JSON.stringify(updatedFilter);
                }
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case UPDATE:
                if (resource === "sendtosap") {
                    url = `${apiUrl}/${resource}/${params.username}`;
                    options.method = 'PUT';
                    options.body = JSON.stringify(params.body);
                }
                else {
                    url = `${apiUrl}/${resource}/${params.id}`;
                    options.method = 'PUT';
                    options.body = setUpdatedInfo(resource, params);
                }
                break;
            case CREATE:
                url = `${apiUrl}/${resource}`;
                options.method = 'POST';
                options.body = setCreateInfo(resource, params);
                break;
            case DELETE:
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = 'DELETE';
                break;
            default:
                throw new Error(`Unsupported fetch action type ${type}`);
        }
        return { url, options };
    };

    const transformSerialToList = (filter) => {
        let ser = filter.serial_number_tim;
        if (ser && typeof ser === "string" && ser.includes(',')) {
            let reg = /^[a-zA-Z0-9%,]*$/;
            if (!reg.test(ser)) {
                throw new Error("Puoi usare solo % , 0-9 A-Z");
            }
            else if (ser.split(',').length > 100) {
                throw new Error("Il filtro contiene più di 100 seriali");
            }
            else filter.serial_number_tim = { "$in": ser.split(',') }
        }
        return JSON.stringify(filter);
    }

    /**
     * @param {Object} response HTTP response from fetch()
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} Data response
     */
    const convertHTTPResponse = (response, type, resource, params) => {
        const { headers, json } = response;
        switch (type) {
            case GET_LIST:
            case GET_MANY_REFERENCE:
            case GET_MANY:
                if (!headers.has('content-range')) {
                    throw new Error(
                        'The Content-Range header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare Content-Range in the Access-Control-Expose-Headers header?'
                    );
                }
                return {
                    data: JSON.parse(JSON.stringify(json.docs).replace(/"_id":/g, '"id":')),
                    total: parseInt(
                        headers
                            .get('content-range')
                            .split('/')
                            .pop(),
                        10
                    ),
                };
            case CREATE:
                if (json.error) {
                    //Duplicate key found
                    if (json.error.errmsg && json.error.errmsg.startsWith('E11000')) {
                        console.log(json.error.errmsg)
                        return new Error('tim.digigemJsonProvider.dup_key');
                    }
                    else {
                        return new Error(json.error.errmsg);
                    }
                }
                else {
                    return { data: { ...params.data, id: json.doc._id } };
                }
            // case GET_MANY:
            //     return { data: JSON.parse(JSON.stringify(json.docs).replace(/"_id":/g, '"id":')) };
            default:
                if (json.id) {
                    return { data: JSON.parse(JSON.stringify(json).replace(/"_id":/g, '"id":')) };
                } else {
                    json.id = params.id;
                    return { data: json };
                }

        }
    };

    /**
     * @param {string} type Request type, e.g GET_LIST
     * @param {string} resource Resource name, e.g. "posts"
     * @param {Object} payload Request parameters. Depends on the request type
     * @returns {Promise} the Promise for a data response
     */
    return (type, resource, params) => {
        // simple-rest doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
        if (type === UPDATE_MANY) {
            return Promise.all(
                params.ids.map(id => {
                    var response = fetch(`${apiUrl}/${resource}/${id}`, {
                        method: 'PUT',
                        body: JSON.stringify(params.data),
                        headers: {
                            'x-zumo-auth': localStorage.getItem('auth-token')
                        }
                    })
                    var jsonResult = response.then(r => r.json());
                    return Promise.all([response, jsonResult]).then(function ([response, jsonResult]) {
                        response.json = jsonResult;
                        return response;
                    })
                })
            ).then(responses => ({
                data: responses.map(response => response.json),
            }));
        }
        // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
        if (type === DELETE_MANY) {
            return Promise.all(
                params.ids.map(id => {
                    var response = fetch(`${apiUrl}/${resource}/${id}`, {
                        method: 'DELETE',
                        headers: {
                            'x-zumo-auth': localStorage.getItem('auth-token')
                        }
                    })
                    var jsonResult = response.then(r => r.json());
                    return Promise.all([response, jsonResult]).then(function ([response, jsonResult]) {
                        response.json = jsonResult;
                        return response;
                    })
                })
            ).then(responses => ({
                data: responses.map(response => response.json),
            }));
        }

        const { url, options } = convertDataRequestToHTTP(
            type,
            resource,
            params
        );

        return httpClient(url, options).then(response =>
            convertHTTPResponse(response, type, resource, params)
        );
    };
};

function setCreateInfo(resource, params) {
    var user = localStorage.getItem('username');
    if (resource === 'devices') {
        params.data['createdBy'] = user;
        params.data['updatedBy'] = user;
    }
    return JSON.stringify(params.data);
}

function setUpdatedInfo(resource, params) {
    var user = localStorage.getItem('username');
    if (resource === 'devices') {
        params.data['updatedBy'] = user;
    }
    return JSON.stringify(params.data);
}